import React, { useState } from "react";

const AccountDeletion = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    reason: "",
    verificationCode: "",
    confirmDeletion: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [apiError, setApiError] = useState("");

  const API_BASE = "https://kids-app.up.railway.app/api/v1";

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear API error when user starts typing
    if (apiError) {
      setApiError("");
    }

    if (name === "email") {
      if (value && !validateEmail(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sendVerificationCode = async () => {
    setIsSubmitting(true);
    setApiError("");

    try {
      const response = await fetch(`${API_BASE}/account-deletion/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          reason: formData.reason || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep(2);
      } else {
        // Handle API errors
        let errorMessage =
          "Failed to send verification code. Please try again.";

        if (response.status === 404) {
          errorMessage = "No account found with this email address.";
        } else if (response.status === 403) {
          errorMessage =
            data.message || "Please wait before requesting a new code.";
        } else if (response.status === 400) {
          errorMessage =
            data.message || "Invalid request. Please check your email address.";
        }

        setApiError(errorMessage);
      }
    } catch (error) {
      console.error("API Error:", error);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDeletion = async () => {
    setIsSubmitting(true);
    setApiError("");

    try {
      // Step 1: Verify the OTP
      const verifyResponse = await fetch(
        `${API_BASE}/account-deletion/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            verificationCode: formData.verificationCode,
          }),
        }
      );

      if (!verifyResponse.ok) {
        let errorMessage = "Verification failed. Please try again.";

        if (verifyResponse.status === 401) {
          errorMessage = "Invalid or expired verification code.";
        } else if (verifyResponse.status === 404) {
          errorMessage = "No pending deletion request found.";
        }

        setApiError(errorMessage);
        return;
      }

      // Step 2: Confirm the deletion (actually delete the account)
      const confirmResponse = await fetch(
        `${API_BASE}/account-deletion/confirm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            verificationCode: formData.verificationCode,
          }),
        }
      );

      const confirmData = await confirmResponse.json();

      if (confirmResponse.ok) {
        setShowSuccess(true);
      } else {
        let errorMessage = "Failed to delete account. Please contact support.";

        if (confirmResponse.status === 404) {
          errorMessage = "Account not found or already deleted.";
        } else if (confirmResponse.status === 400) {
          errorMessage = confirmData.message || "Failed to delete account.";
        }

        setApiError(errorMessage);
      }
    } catch (error) {
      console.error("API Error:", error);
      setApiError("Network error. Please contact support or try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendVerificationCode = async () => {
    setIsSubmitting(true);
    setApiError("");

    try {
      const response = await fetch(`${API_BASE}/account-deletion/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          reason: formData.reason || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setApiError("");
        // Show success message for resend
        setApiError(""); // Clear any existing errors
      } else {
        let errorMessage = "Failed to resend verification code.";

        if (response.status === 403) {
          errorMessage =
            data.message || "Please wait before requesting a new code.";
        }

        setApiError(errorMessage);
      }
    } catch (error) {
      console.error("API Error:", error);
      setApiError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-10 bg-gray-50 min-h-screen">
        <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h1 className="text-2xl font-bold text-green-800 mb-4">
            Account Successfully Deleted
          </h1>
          <p className="text-green-700 mb-6">
            Your account has been permanently deleted. You will receive a
            confirmation email shortly.
          </p>
          <div className="bg-white p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">
              Deletion Process:
            </h3>
            <ul className="text-left text-green-700 space-y-1">
              <li>• Account access: Immediately disabled</li>
              <li>• Active data: Removed within 3 business days</li>
              <li>• Backup data: Permanently deleted within 30 days</li>
              <li>• Confirmation email: Sent to your inbox</li>
            </ul>
          </div>

          <div className="mt-6">
            <p className="text-gray-600 text-sm">
              Thank you for using mostaqbal kids. We're sorry to see you go.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 py-10 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-700 mb-2">
          حذف الحساب - Delete Account
        </h1>
        <h2 className="text-xl text-blue-600 mb-4">
          طفل المستقبل - mostaqbal kids
        </h2>
        <p className="text-gray-600">
          Request deletion of your account and all associated data
        </p>
      </div>

      {/* Important Notice */}
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8 rounded-lg">
        <h3 className="font-semibold text-red-800 mb-2">⚠️ Important Notice</h3>
        <p className="text-red-700 text-sm">
          Account deletion is permanent and cannot be undone. All learning
          progress, achievements, and account data will be permanently removed
          from our servers.
        </p>
      </div>

      {/* API Error Display */}
      {apiError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">❌</span>
            </div>
            <div className="ml-3">
              <p className="text-red-700 text-sm">{apiError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
              step >= 1 ? "bg-blue-500" : "bg-gray-300"
            }`}
          >
            1
          </div>
          <div
            className={`w-16 h-1 ${step >= 2 ? "bg-blue-500" : "bg-gray-300"}`}
          ></div>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
              step >= 2 ? "bg-blue-500" : "bg-gray-300"
            }`}
          >
            2
          </div>
        </div>
      </div>

      {step === 1 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-xl font-semibold mb-4 text-slate-700">
            Step 1: Account Information
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  emailError ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="your-email@example.com"
                required
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-600">{emailError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Deletion (Optional)
              </label>
              <select
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a reason...</option>
                <option value="no-longer-needed">No longer needed</option>
                <option value="privacy-concerns">Privacy concerns</option>
                <option value="switching-apps">
                  Switching to different app
                </option>
                <option value="technical-issues">Technical issues</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                name="confirmDeletion"
                checked={formData.confirmDeletion}
                onChange={handleInputChange}
                className="mt-1 mr-3"
                required
              />
              <label className="text-sm text-gray-700">
                I understand that this action is permanent and will delete all
                account data, learning progress, and achievements. This action
                cannot be undone. *
              </label>
            </div>
          </div>

          <button
            onClick={sendVerificationCode}
            disabled={
              !formData.email ||
              !validateEmail(formData.email) ||
              !formData.confirmDeletion ||
              isSubmitting
            }
            className="w-full mt-6 bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting
              ? "Sending Verification Code..."
              : "Send Verification Code"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-xl font-semibold mb-4 text-slate-700">
            Step 2: Email Verification & Account Deletion
          </h3>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-blue-800 text-sm">
              We've sent a verification code to{" "}
              <strong>{formData.email}</strong>. Please check your email and
              enter the 6-digit code below to complete the deletion process.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code *
              </label>
              <input
                type="text"
                name="verificationCode"
                value={formData.verificationCode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                placeholder="1111"
                maxLength="4"
                required
              />
            </div>

            <div className="text-center">
              <button
                onClick={resendVerificationCode}
                disabled={isSubmitting}
                className="text-blue-600 hover:text-blue-800 text-sm underline disabled:text-gray-400"
              >
                {isSubmitting ? "Resending..." : "Resend verification code"}
              </button>
            </div>
          </div>

          <div className="flex space-x-4 mt-6">
            <button
              onClick={() => setStep(1)}
              disabled={isSubmitting}
              className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors"
            >
              Back
            </button>
            <button
              onClick={submitDeletion}
              disabled={
                !formData.verificationCode ||
                formData.verificationCode.length !== 4 ||
                isSubmitting
              }
              className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting
                ? "Deleting Account..."
                : "Confirm & Delete Account"}
            </button>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold mb-3 text-slate-700">
          Data Deletion Timeline
        </h3>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
              1
            </div>
            <div>
              <strong>Immediate:</strong> Account access is disabled
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
              2
            </div>
            <div>
              <strong>Within 3 days:</strong> Account data is removed from
              active systems
            </div>
          </div>
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
              3
            </div>
            <div>
              <strong>Within 30 days:</strong> All data is permanently deleted
              from backups and logs
            </div>
          </div>
        </div>
      </div>

      {/* Contact Support */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Need help? Contact our support team at{" "}
          <a
            href="mailto:support@mostaqbalkids.com"
            className="text-blue-600 hover:text-blue-800"
          >
            support@mostaqbalkids.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default AccountDeletion;
